// src/pages/FaqPage.tsx
//
// ★ PHASE 11 — Annex A story 39, "General FAQ or Enquiry Chatbot". A scoped
// keyword match over this product's own FAQ content — never a call to an
// external LLM (P2C11 §3.6). See `routes/faq.ts`'s own header.
//
// ★★ WAVE 8 (Task 5) — THE STORY'S NAME AND THE PRODUCT'S NAME ARE NOW TWO
// DIFFERENT STRINGS, deliberately.
//
// "General FAQ or Enquiry Chatbot" above is the AUTHORITY'S OWN WORD for the
// requirement and stays quoted wherever this code cites Annex A. What the
// PRODUCT calls itself is **"FAQ keyword search"** — on the heading, in the
// navigation rail and in the breadcrumb (`components/Shell.tsx`) — because
// "chatbot" would promise a conversation this cannot hold and an
// understanding it does not have. The one line saying why is on the screen
// itself, not only in this comment.
//
// ⚠ The ROUTE is still `/faq`, and that is a decision rather than an
// oversight: renaming a path breaks every link an officer has already saved,
// which is a different and worse kind of change than fixing a label. The
// route is `/faq`, the API is `/api/faq`, and neither claims to be a chatbot.
import { useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { targetValue } from '../lib/sgds-dom'
import type { FaqAskResponse } from '../lib/types'

export function FaqPage() {
  const [question, setQuestion] = useState('')
  const [asked, setAsked] = useState<string | null>(null)
  const [answer, setAnswer] = useState<FaqAskResponse | null>(null)
  const [asking, setAsking] = useState(false)
  const [askError, setAskError] = useState<string | null>(null)

  const entries = useApi(() => api.faqEntries(), [])

  /**
   * ★★ WAVE 8 (Task 5) — THE SIZE OF THE CORPUS COMES FROM THE PAYLOAD, never
   * from a numeral typed into this file.
   *
   * `GET /api/faq` returns exactly the entries `visibleFaqEntries(role)` admits
   * and `POST /api/faq/ask` scores the SAME set, so this length IS the search
   * corpus for the officer reading it — and it is smaller for some roles than
   * others.
   *
   * ⚠ Why that matters here rather than being a style point: both
   * `docs/delivery/WAVE-8-PLAN.md` and this task's own brief describe this
   * feature as "eight fixed entries". `faq-content.ts` holds **seven**, and a
   * role-filtered view can be as small as **three** (an `MBIM_USER` sees only
   * the three entries marked `ALL`). A hand-written figure here would have put
   * one of those wrong numbers on the screen.
   */
  const corpusSize = entries.data?.entries.length

  /**
   * The phrase, not the number, so 0 and 1 do not render as *"the 0 fixed
   * answers"* / *"the 1 fixed answers"*.
   *
   * ⚠ Every one of the eight human roles `mayUseFaq` admits currently sees
   * between three and six (measured), and only the two MACHINE identities see
   * none — and they are refused the route. So the zero branch is unreachable
   * today; it exists because a role added later with no `ALL`-marked entry would
   * reach it, and *"the 0 fixed answers"* is the kind of sentence that ships.
   */
  const corpusPhrase =
    corpusSize === undefined
      ? 'a fixed set of answers your role can see'
      : corpusSize === 0
        ? 'a fixed set of answers, none of which is visible to your role'
        : corpusSize === 1
          ? 'the single fixed answer your role can see'
          : `the ${corpusSize} fixed answers your role can see`

  const handleAsk = async (event: FormEvent) => {
    event.preventDefault()
    if (question.trim().length === 0) return
    setAsking(true)
    setAskError(null)
    try {
      const res = await api.askFaq(question.trim())
      setAnswer(res)
      setAsked(question.trim())
    } catch (error) {
      setAskError(error instanceof Error ? error.message : 'Could not reach the FAQ.')
    } finally {
      setAsking(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">FAQ keyword search</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          General questions and answers. Type a question in your own words, or
          browse the list below.
          {' '}
          <strong>This is not a chatbot:</strong> it scores the words you type against{' '}
          {corpusPhrase}, so it matches on keywords rather than on meaning and it never writes a sentence of its
          own.
          Phrasing a question differently can change what it finds, an unfamiliar topic simply will not match,
          and browsing the list is often faster.
          {' '}
          Nothing you type leaves this process. An AI assistant is a different feature and is not offered:
          classified information and personal data may not be sent outside Singapore without prior written
          approval, which rules out the obvious implementation on real data.
        </p>
      </div>

      <form className="pt-card flex items-end gap-3 flex-wrap" onSubmit={handleAsk}>
        <div className="flex-1 min-w-[240px]">
          <SgdsInput
            label="Search the FAQ in your own words"
            value={question}
            onSgdsInput={(e) => setQuestion(targetValue(e))}
            placeholder="e.g. How do I check officer entitlement?"
          />
        </div>
        <SgdsButton type="submit" variant="primary" disabled={question.trim().length === 0} loading={asking}>
          Search
        </SgdsButton>
      </form>

      {askError !== null && <ErrorCard title="Could not search the FAQ" message={askError} />}

      {asked !== null && answer !== null && (
        <div className="pt-card">
          <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-3">You searched for: &ldquo;{asked}&rdquo;</p>
          {answer.matched && answer.answers !== undefined && answer.answers.length > 0 ? (
            <div className="flex flex-col gap-4">
              {answer.answers.map((a) => (
                <div key={a.id}>
                  <p className="font-semibold">{a.question}</p>
                  <p>{a.answer}</p>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <p>{answer.message}</p>
              {answer.suggestions !== undefined && answer.suggestions.length > 0 && (
                <ul className="mt-3 list-disc pl-5">
                  {answer.suggestions.map((s) => (
                    <li key={s.id}>{s.question}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <div>
        <h2 className="text-lg mb-3">Browse all FAQ entries</h2>
        {entries.error !== null ? (
          <ErrorCard title="Could not load the FAQ" message={entries.error} onRetry={entries.reload} />
        ) : entries.initialLoading ? (
          <LoadingCard label="Loading FAQ" rows={4} />
        ) : (entries.data?.entries.length ?? 0) === 0 ? (
          <EmptyCard title="No FAQ entries" message="Nothing is visible to your role yet." />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {entries.data?.entries.map((entry) => (
              <div key={entry.id} className="pt-card">
                <p className="font-semibold mb-1">{entry.question}</p>
                <p className="text-sm text-[var(--sgds-body-color-subtle)]">{entry.answer}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
